

import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Typography from '@material-ui/core/Typography';


import { styles } from "./styles";
import UnlockShiptoManagementAPI from "apis/UnlockShiptoManagement/UnlockShiptoManagement";
import Context from "context/context";

import { withStyles, makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import TextField from "@material-ui/core/TextField";
import InputLabel from "@material-ui/core/InputLabel";


import Chip from "@material-ui/core/Chip";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import InfoIcon from "@material-ui/icons/Info";

// ─── Override options by current review status ─────────────────────────────
// Keys = current review_status from API
// Values = radio options to show (always starts with "No Override")
const OVERRIDE_OPTIONS_MAP = {
  Eligible:        ["No Override", "Hard Eligible", "Hard Ineligible"],
  Ineligible:      ["No Override",  "Hard Eligible", "Hard Ineligible"],
  "Hard Eligible": ["No Override", "Hard Ineligible"],
  "Hard Ineligible": ["No Override",  "Hard Eligible"],
  "Rejected": ["No Override",  "Hard Eligible", "Hard Ineligible"],
  Inactive:        ["No Override", "Eligible", "Hard Eligible", "Hard Ineligible"],
};

// Compute what the "Effective Program Eligibility" will be after applying the override
function getEffectiveEligibility(reviewStatus, override) {
  if (override === "No Override") return reviewStatus;
  if (override === "Hard Eligible") return "Eligible";
  if (override === "Hard Ineligible") return "Ineligible";
  return override; // "Eligible" or "Ineligible" overrides map directly
}

const eligibilityColor = (val) => {
  if (val === "Eligible" || val === "Hard Eligible" || val === "Aprroved") return "#1D9E75";
  if (val === "Ineligible" || val === "Hard Ineligible" || val === "Rejected") return "#D85A30";
  if (val === "Inactive") return "#888";
  return "#305698";
};

const useLocalStyles = makeStyles({
  infoPanel: {
    border: "1px solid #305698",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 16,
    background: "#f5f8ff",
  },
  infoPanelHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
    color: "#305698",
    fontWeight: 600,
    fontSize: 14,
  },
  infoBullet: {
    fontSize: 12,
    color: "#555",
    marginBottom: 4,
    lineHeight: 1.6,
  },
  effectiveBox: {
    border: "1px solid #d0e8d0",
    borderRadius: 8,
    padding: "10px 16px",
    background: "#f0f7f0",
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  recordBadge: {
    fontSize: 11,
    fontWeight: 600,
    marginLeft: 8,
    verticalAlign: "middle",
  },
});

export default function Screen2AddEligibilityRecord() {
  const { dispatch } = useContext(Context);
  const { saveShiptoEligibility } = UnlockShiptoManagementAPI();

  const history = useHistory();
  const location = useLocation();
  const classes = styles();
  const local = useLocalStyles();
  const { t } = useTranslation();

  // customerData comes from Screen 1 via location.state
  const customerData = location?.state?.customerData;

  const reviewStatus = customerData?.review_status || "Eligible";
  const overrideOptions = OVERRIDE_OPTIONS_MAP[reviewStatus] || OVERRIDE_OPTIONS_MAP["Eligible"];

  const [override, setOverride] = useState("No Override");
  const [loading, setLoading] = useState(false);
  const [reason, setreason] = useState(customerData?.reason);

  useEffect(() => {
    dispatch({
      type: "SET_PATHNAME",
      payload: ["HOME", "ADMIN", "Unlock Account Management", "Create"],
    });
    // Reset to "No Override" when data changes
    setOverride("No Override");
  }, [dispatch, reviewStatus]);

  // If navigated here without data, redirect back
  useEffect(() => {
    if (!customerData) {
      toast.error("No customer data found. Please search again.");
      history.replace("/dashboard/unlock-create-shipto");
    }
  }, [customerData, history]);

  const effectiveEligibility = getEffectiveEligibility(reviewStatus, override);
  const isExistingRecord = customerData?.source === "unlock_shipto_management";

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        shipto: customerData.shipto,
        override,
        effectiveEligibility,
        isExisting: isExistingRecord,
        reason
      };

      // POST /api/unlock-shipto/save  (see backend save() method below)
      const res = await saveShiptoEligibility(payload);

      if (res?.success === true) {
        toast.success(res?.message || "Saved successfully");
        history.replace("/dashboard/unlock-create-shipto", {});
      } else {
        toast.error(res?.message || "Failed to save");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [customerData, override, effectiveEligibility, isExistingRecord,reason, saveShiptoEligibility, history]);

  if (!customerData) return null;

  return (
    <React.Fragment>
      {/* Header */}
      <Grid container spacing={0} style={{ padding: 10 }}>
        <Grid item xs={12}>
          <IconButton onClick={() => history.replace("/dashboard/unlock-create-shipto", {})}>
            <ArrowBackIcon style={{ color: "#305698" }} />
          </IconButton>
          <span style={{ fontSize: 18, fontWeight: "bold" }}>{t("Create")}</span>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ padding: "0 10px" }}>
        {/* ── Customer Info Card ─────────────────────────────── */}
        <Grid item xs={12}>
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              padding: "14px 18px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px 24px",
              marginBottom: 16,
              background: "#fff",
            }}
          >
            <div>
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                Customer Account Location:
              </span>{" "}
              <span style={{ fontSize: 13, color: "#555" }}>
                {customerData.shipto}
              </span>
              <Chip
                label={isExistingRecord ? "Existing" : "New"}
                size="small"
                className={local.recordBadge}
                style={{
                  background: isExistingRecord ? "#EAF3DE" : "#E6F1FB",
                  color: isExistingRecord ? "#3B6D11" : "#185FA5",
                  height: 20,
                  fontSize: 11,
                }}
              />
            </div>
            <div>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Sales Account Status:</span>{" "}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: eligibilityColor(customerData.account_sales_status),
                }}
              >
                {customerData.account_sales_status}
              </span>
              <InfoIcon style={{ fontSize: 16, color: "#305698", verticalAlign: "middle", marginLeft: 4 }} />
            </div>
            <div>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Customer Name:</span>{" "}
              <span style={{ fontSize: 13, color: "#555" }}>{customerData.practice_name}</span>
            </div>
            <div>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Program Eligibility:</span>{" "}
              <span style={{ fontSize: 13, fontWeight: 600, color: eligibilityColor(reviewStatus) }}>
                {reviewStatus}
              </span>
            </div>
             <div>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Program Status:</span>{" "}
              <span style={{ fontSize: 13, color: "#555" }}>{customerData.program_type}</span>
            </div>
          </div>
        </Grid>

        {customerData.program_type !== 'Signature' &&  customerData.program_type !== 'Thrive Startup' &&  customerData.program_type !== 'Thrive Unlock'?( <>

        {/* ── Eligibility Override Radio Group (dynamic) ─────── */}
        <Grid item xs={12}>
          <FormControl component="fieldset" style={{ marginBottom: 16 }}>
            <FormLabel
              component="legend"
              style={{ fontSize: 14, fontWeight: 600, color: "#222", marginBottom: 8 }}
            >
              {t("Eligibility Override:")}
            </FormLabel>
            <RadioGroup
              value={override}
              onChange={(e) => setOverride(e.target.value)}
            >
              {overrideOptions.map((opt) => (
                <FormControlLabel
                  key={opt}
                  value={opt}
                  control={<Radio size="small" style={{ color: "#305698" }} />}
                  label={<span style={{ fontSize: 14 }}>{opt}</span>}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
  <div style={{ marginBottom: 16 }}>
    <div style={{ marginTop: 10 }}>
      <InputLabel
        htmlFor="comments"
        className={classes.labelTxt}
        style={{marginBottom:10}}
      >
        Program Admin Reason Description
      </InputLabel>

      <TextField
        id="comments"
        type="text"
        variant="outlined"
        autoComplete="off"
        className={classes.searchInput}
        style={{ width: "50%" }}
        value={reason}
        name="comments"
        inputProps={{ maxLength: 50 }}
        onChange={(e) => setreason(e.target.value)}
      />
    </div>
  </div>
</Grid>

        {/* ── Info Panel ─────────────────────────────────────── */}
        <Grid item xs={12}>
          <div className={local.infoPanel}>
            <div className={local.infoPanelHeader}>
              <InfoIcon style={{ fontSize: 20, color: "#305698" }} />
              Override Details – How it Works
            </div>
            <p className={local.infoBullet}>
              <strong>Manual Lock:</strong> Use an override only when eligibility needs to be manually locked.
            </p>
            <p className={local.infoBullet}>
              <strong>Daily Rule Protection:</strong> Hard overrides prevent the daily eligibility process from changing this account's program eligibility.
            </p>
            <p className={local.infoBullet}>
              <strong>1-Year Lock:</strong> Hard overrides remain locked for one year from the save date.
            </p>
            <p className={local.infoBullet}>
              <strong>System Reversion:</strong> After one year, the override expires and the account returns to system-managed daily eligibility evaluation.
            </p>
          </div>
        </Grid>

        {/* ── Effective Program Eligibility ─────────────────── */}
        <Grid item xs={12}>
          <div className={local.effectiveBox}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>
              Effective Program Eligibility:
            </span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: eligibilityColor(effectiveEligibility),
              }}
            >
              {effectiveEligibility}
            </span>
          </div>
        </Grid>

        {/* ── Action Buttons ─────────────────────────────────── */}
        <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <Button
            variant="outlined"
            style={{ color: "#305698", borderColor: "#305698" }}
            onClick={() => history.replace("/dashboard/unlock-create-shipto", {})}
          >
            {t("Cancel")}
          </Button>
          <Button
            className={classes.submitBtn}
            disabled={loading}
            onClick={handleSave}
          >
            {loading ? "Saving..." : t("Save")}
          </Button>
        </Grid>
        </>):<> <Grid item xs={12}>
    <Typography align="center">
      This member is already an active{' '}
      <strong>{customerData.program_type}</strong>{' '}
       member
    </Typography>
  </Grid></>}
      </Grid>
    </React.Fragment>
  );
}